import { Component, Input, OnInit, ChangeDetectorRef, inject } from '@angular/core';
import { LogError, RunView } from '@memberjunction/core';
import { PerformanceMetricRead, CompanyIntegrationRow } from '../../marketing-workspace.component';
import { RoleTier } from '../../role-filter';
import { runAction } from '../../shared/run-action.util.js';

interface AnalyticsPullRunRow {
    ID: string;
    Status: string;
    StartedAt: string | Date | null;
    CompletedAt: string | Date | null;
    ErrorMessage: string | null;
    Success: boolean | null;
}

type BannerVariant = 'idle' | 'ok' | 'warn' | 'fail' | 'running';

type SourceStatus = 'healthy' | 'connected' | 'pending' | 'unconnected' | 'gap';

interface SourceCard {
    name: string;
    label: string;
    sub: string;
    icon: string;
    /** Integration name in __mj.Integration.Name (for CompanyIntegration matching) */
    integration: string | null;
    /** Platform value in ContentPerformanceMetric.Platform (for live data matching). null = no metrics surface. */
    platform: string | null;
    /** Static "why this exists" hint shown when no live data */
    blurb: string;
    /** Status fallback when no live data + no integration row */
    fallbackStatus: SourceStatus;
}

const SOURCE_CATALOG: SourceCard[] = [
    { name: 'ga4',      label: 'GA4 · Web analytics',     sub: 'Per-blog pageviews, engagement time, conversions', icon: 'fa-chart-column', integration: 'GA4',      platform: 'GA4',      blurb: 'Add a CompanyIntegration row + GA4_SA_JSON env var to enable.', fallbackStatus: 'unconnected' },
    { name: 'linkedin', label: 'LinkedIn Analytics',      sub: 'Marketing Developer Platform · OAuth',             icon: 'fa-briefcase',    integration: 'LinkedIn', platform: 'LinkedIn', blurb: 'MDP approval pending — provider scaffold ready, see board card #808.', fallbackStatus: 'pending' },
    { name: 'buffer',   label: 'Buffer',                  sub: 'Cross-platform social · post-level metrics',       icon: 'fa-paper-plane',  integration: 'Buffer',   platform: 'Buffer',   blurb: 'Connected for publishing; analytics flow via bizapps action.', fallbackStatus: 'connected' },
    // Integration key 'HubSpot AIDP' = the legacy CDP integration row (renamed; 'HubSpot'
    // now names MJ's connector row — plans/HubSpot-Rename-To-HubSpot-AIDP-Diagnosis.md).
    // Platform follows the rename: new ContentPerformanceMetric rows carry 'HubSpot AIDP'.
    { name: 'hubspot',  label: 'HubSpot · CMS & CRM',     sub: 'Publishing + contact attribution',                 icon: 'fa-envelope',     integration: 'HubSpot AIDP',  platform: 'HubSpot AIDP',  blurb: 'Analytics API requires Pro+ tier. Workaround: GA4 covers blog performance.', fallbackStatus: 'gap' },
    // HubSpot deal pipeline (#934). Surfaces ingest health for marketing.Deal /
    // marketing.DealStageEvent. No Platform on ContentPerformanceMetric — deal
    // funnel volume is rendered by mkt-ws-analytics-deal-pipeline below.
    { name: 'hubspot-deals', label: 'HubSpot · Deal pipeline', sub: 'Lead → MQL → SQL → Opportunity → Closed-Won', icon: 'fa-funnel-dollar', integration: 'HubSpot AIDP', platform: null, blurb: 'Daily 6am CT pull (Marketing - HubSpot Deals Pull). Disabled in stage until prod row exists.', fallbackStatus: 'pending' },
];

/** Stage-bucket counts derived from crm.Deal.DealStage names. */
type DealStage = 'Lead' | 'MQL' | 'SQL' | 'Opportunity' | 'ClosedWon' | 'ClosedLost';

interface CrmDealRead {
    ID: string;
    ExternalSystemRecordID: string | null;
    DealStage: string | null;
    Value: number | null;
    CompanyIntegrationID: string | null;
}

interface MarketingSidecarRead {
    ID: string;
    ExternalDealID: string;
    LastSyncedAt: string | Date | null;
    AttributionJSON: string | null;
}

/**
 * Map a `crm.DealStage.Name` to the CDP funnel enum. Best-effort match
 * across HubSpot defaults and Sidecar's custom pipelines (Discover →
 * MQL, Develop → SQL, Propose/Agreement → Opportunity, etc.). Anything
 * unrecognized falls into Opportunity to avoid losing it from the
 * funnel; we don't currently surface a "Custom stage" bucket.
 */
function mapCrmStageNameToFunnel(name: string | null | undefined): DealStage | null {
    if (!name) return null;
    const lower = name.toLowerCase().trim();
    if (lower.includes('won')) return 'ClosedWon';
    if (lower.includes('lost')) return 'ClosedLost';
    if (lower === 'lead' || lower.includes('appointment')) return 'Lead';
    if (lower.includes('mql') || lower.includes('qualified') || lower === 'discover') return 'MQL';
    if (lower.includes('sql') || lower === 'develop' || lower.includes('presentation') || lower === 'validate') return 'SQL';
    return 'Opportunity';
}

interface AttributionConfigRead {
    ID: string;
    AttributionModel: string;
    FirstTouchWeight: number;
    MiddleTouchWeight: number;
    LastTouchWeight: number;
}

interface ResolvedCard extends SourceCard {
    status: SourceStatus;
    rows7d: number | null;
    rowsTotal: number | null;
    lastSyncDisplay: string;
    healthBadge: { text: string; variant: 'good' | 'warn' | 'bad' | 'muted' | 'info' };
}

/**
 * Analytics > Data Sources — real provider status panel.
 *
 * Hydrates a static source catalog with live signal:
 *   • CompanyIntegration → connected/active state
 *   • ContentPerformanceMetric → has-data, last sync, rows-this-week
 *
 * Mockup uses a hardcoded list (#analytics/data-sources). This component is the
 * editable, real-data version. New sources land here by adding a SOURCE_CATALOG
 * entry; live data shows up automatically once their provider writes rows.
 */
@Component({
    standalone: false,
    selector: 'mkt-ws-analytics-data-sources',
    template: `
<mkt-ws-page-head
    eyebrow="Analytics · Data sources & ingest"
    title="Where the numbers come from."
    sub="Provider abstraction over each analytics source. Portfolio companies swap providers; the metrics tables stay the same.">
</mkt-ws-page-head>

<div class="ds__banner" [class]="'ds__banner--' + bannerVariant">
    <div class="ds__banner-icon"><i class="fa-solid" [class]="bannerIcon"></i></div>
    <div class="ds__banner-text">
        <div class="ds__banner-title">{{ bannerTitle }}</div>
        <div class="ds__banner-sub">{{ bannerSub }}</div>
    </div>
    <button class="ds__pull"
            (click)="onPullNow()"
            [disabled]="!companyId || pulling">
        <i class="fa-solid" [class.fa-arrow-down-to-line]="!pulling" [class.fa-spinner]="pulling" [class.fa-spin]="pulling"></i>
        {{ pulling ? 'Pulling…' : 'Pull now' }}
    </button>
</div>

<div class="ds__grid">
    <div *ngFor="let c of cards" class="ds__card">
        <div class="ds__hd">
            <div class="ds__icon"><i class="fa-solid" [class]="c.icon"></i></div>
            <div class="ds__title">
                <div class="ds__name">{{ c.label }}</div>
                <div class="ds__sub">{{ c.sub }}</div>
            </div>
            <mkt-ws-pill [variant]="c.healthBadge.variant" [dot]="true">{{ c.healthBadge.text }}</mkt-ws-pill>
        </div>

        <div class="ds__body">
            <div class="ds__row">
                <span class="ds__k">Last sync</span>
                <span class="ds__v">{{ c.lastSyncDisplay }}</span>
            </div>
            <div class="ds__row" *ngIf="c.rows7d != null">
                <span class="ds__k">Rows ingested · 7d</span>
                <span class="ds__v">{{ c.rows7d | number }}</span>
            </div>
            <div class="ds__row" *ngIf="c.rowsTotal != null && c.rows7d == null">
                <span class="ds__k">Rows · all-time</span>
                <span class="ds__v">{{ c.rowsTotal | number }}</span>
            </div>
            <div class="ds__row" *ngIf="c.status === 'pending' || c.status === 'unconnected' || c.status === 'gap'">
                <span class="ds__k">Note</span>
                <span class="ds__v ds__v--muted">{{ c.blurb }}</span>
            </div>
        </div>
    </div>
</div>

<!-- Deal pipeline panel (#934) — funnel + attribution model summary -->
<div class="dp" *ngIf="dealStats">
    <div class="dp__hd">
        <div>
            <div class="dp__title">HubSpot deal pipeline</div>
            <div class="dp__sub">Lead → MQL → SQL → Opportunity → Closed-Won. Per-stage drop-off + multi-touch attribution. Updated daily.</div>
        </div>
        <div class="dp__attr">
            <span class="dp__k">Attribution</span>
            <span class="dp__v">{{ attributionLabel }}</span>
        </div>
    </div>
    <div class="dp__funnel">
        <div *ngFor="let s of stageCards" class="dp__stage">
            <div class="dp__stage-label">{{ s.label }}</div>
            <div class="dp__stage-count">{{ s.count | number }}</div>
            <div class="dp__stage-amt" *ngIf="s.amount != null">\${{ s.amount | number:'1.0-0' }}</div>
            <div class="dp__stage-drop" *ngIf="s.dropoff != null">
                <i class="fa-solid fa-arrow-right-from-bracket"></i>
                {{ s.dropoff | percent:'1.0-1' }} drop
            </div>
        </div>
    </div>
    <div class="dp__totals">
        <span class="dp__k">Total deals</span><span class="dp__v">{{ dealStats.totalDeals | number }}</span>
        <span class="dp__k">Closed-Won pipeline</span><span class="dp__v">\${{ dealStats.wonAmount | number:'1.0-0' }}</span>
        <span class="dp__k">Lead → Won conversion</span><span class="dp__v">{{ dealStats.leadToWon | percent:'1.1-1' }}</span>
    </div>
</div>

<div class="ds__footer">
    Add a new source by registering an Integration row + a CompanyIntegration for this company,
    then catalog it in <code>SOURCE_CATALOG</code> alongside the existing entries.
</div>
    `,
    styles: [`
        :host { display: block; }

        .ds__banner { display: flex; align-items: center; gap: 14px; padding: 14px 18px; border-radius: 10px; border: 1px solid var(--mj-border-default, #e0e0e0); margin-bottom: 18px; background: var(--mj-bg-surface, #fff); }
        .ds__banner--ok { border-color: color-mix(in srgb, var(--mj-color-success-500, #16a34a) 35%, transparent); background: color-mix(in srgb, var(--mj-color-success-500, #16a34a) 8%, var(--mj-bg-surface, #fff)); }
        .ds__banner--warn { border-color: color-mix(in srgb, var(--mj-color-warning-500, #f59e0b) 35%, transparent); background: color-mix(in srgb, var(--mj-color-warning-500, #f59e0b) 8%, var(--mj-bg-surface, #fff)); }
        .ds__banner--fail { border-color: color-mix(in srgb, var(--mj-color-danger-500, #dc2626) 35%, transparent); background: color-mix(in srgb, var(--mj-color-danger-500, #dc2626) 8%, var(--mj-bg-surface, #fff)); }
        .ds__banner--running { border-color: color-mix(in srgb, var(--mj-color-info-500, #2563eb) 35%, transparent); background: color-mix(in srgb, var(--mj-color-info-500, #2563eb) 8%, var(--mj-bg-surface, #fff)); }

        .ds__banner-icon { width: 32px; height: 32px; border-radius: 6px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; background: color-mix(in srgb, var(--mj-text-secondary, #71717a) 10%, transparent); color: var(--mj-text-secondary, #71717a); }
        .ds__banner--ok .ds__banner-icon { color: var(--mj-color-success-500, #16a34a); background: color-mix(in srgb, var(--mj-color-success-500, #16a34a) 12%, transparent); }
        .ds__banner--warn .ds__banner-icon { color: var(--mj-color-warning-500, #f59e0b); background: color-mix(in srgb, var(--mj-color-warning-500, #f59e0b) 12%, transparent); }
        .ds__banner--fail .ds__banner-icon { color: var(--mj-color-danger-500, #dc2626); background: color-mix(in srgb, var(--mj-color-danger-500, #dc2626) 12%, transparent); }
        .ds__banner--running .ds__banner-icon { color: var(--mj-color-info-500, #2563eb); background: color-mix(in srgb, var(--mj-color-info-500, #2563eb) 12%, transparent); }

        .ds__banner-text { flex: 1; min-width: 0; }
        .ds__banner-title { font-size: 13px; font-weight: 600; color: var(--mj-text-primary, #1a1a1a); }
        .ds__banner-sub { font-size: 11.5px; color: var(--mj-text-secondary, #71717a); margin-top: 2px; line-height: 1.4; word-break: break-word; }

        .ds__pull { display: inline-flex; align-items: center; gap: 6px; padding: 7px 14px; border: 1px solid var(--mj-border-default, #e0e0e0); border-radius: 6px; background: var(--mj-bg-surface, #fff); font-size: 12px; font-weight: 500; color: var(--mj-text-primary, #1a1a1a); cursor: pointer; flex-shrink: 0; }
        .ds__pull:hover:not(:disabled) { background: var(--mj-bg-page, #f5f5f5); }
        .ds__pull:disabled { opacity: 0.5; cursor: not-allowed; }
        .ds__pull i { font-size: 11px; }

        .ds__grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin-bottom: 24px; }
        @media (max-width: 1100px) { .ds__grid { grid-template-columns: repeat(2, 1fr); } }
        @media (max-width: 700px) { .ds__grid { grid-template-columns: 1fr; } }

        .ds__card { background: var(--mj-bg-surface, #fff); border: 1px solid var(--mj-border-default, #e0e0e0); border-radius: 10px; padding: 18px 20px; }
        .ds__hd { display: flex; align-items: center; gap: 12px; margin-bottom: 14px; }
        .ds__icon { width: 32px; height: 32px; border: 1px solid var(--mj-border-default, #e0e0e0); border-radius: 6px; display: flex; align-items: center; justify-content: center; color: var(--mj-text-secondary, #71717a); }
        .ds__icon i { font-size: 14px; }
        .ds__title { flex: 1; min-width: 0; }
        .ds__name { font-weight: 500; font-size: 13.5px; color: var(--mj-text-primary, #1a1a1a); line-height: 1.2; }
        .ds__sub { font-size: 11px; color: var(--mj-text-secondary, #71717a); margin-top: 2px; line-height: 1.3; }

        .ds__body { display: flex; flex-direction: column; gap: 6px; padding-top: 12px; border-top: 1px solid var(--mj-border-default, #e0e0e0); font-size: 12px; }
        .ds__row { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; }
        .ds__k { color: var(--mj-text-secondary, #71717a); flex-shrink: 0; }
        .ds__v { font-family: 'JetBrains Mono', ui-monospace, monospace; font-variant-numeric: tabular-nums; color: var(--mj-text-primary, #1a1a1a); text-align: right; }
        .ds__v--muted { color: var(--mj-text-secondary, #71717a); font-family: inherit; font-size: 11.5px; line-height: 1.4; }

        .dp { background: var(--mj-bg-surface, #fff); border: 1px solid var(--mj-border-default, #e0e0e0); border-radius: 10px; padding: 18px 20px; margin-bottom: 18px; }
        .dp__hd { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; margin-bottom: 14px; }
        .dp__title { font-weight: 600; font-size: 14px; color: var(--mj-text-primary, #1a1a1a); }
        .dp__sub { font-size: 11.5px; color: var(--mj-text-secondary, #71717a); margin-top: 2px; }
        .dp__attr { font-size: 11.5px; text-align: right; flex-shrink: 0; }
        .dp__attr .dp__k { display: block; color: var(--mj-text-secondary, #71717a); font-size: 10.5px; }
        .dp__attr .dp__v { font-weight: 600; font-family: 'JetBrains Mono', ui-monospace, monospace; }
        .dp__funnel { display: grid; grid-template-columns: repeat(6, 1fr); gap: 8px; padding: 12px 0; border-top: 1px solid var(--mj-border-default, #e0e0e0); border-bottom: 1px solid var(--mj-border-default, #e0e0e0); margin-bottom: 12px; }
        @media (max-width: 900px) { .dp__funnel { grid-template-columns: repeat(3, 1fr); } }
        .dp__stage { padding: 8px 10px; background: var(--mj-bg-page, #f7f7f9); border-radius: 6px; }
        .dp__stage-label { font-size: 10.5px; color: var(--mj-text-secondary, #71717a); text-transform: uppercase; letter-spacing: 0.04em; }
        .dp__stage-count { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 18px; font-weight: 600; color: var(--mj-text-primary, #1a1a1a); }
        .dp__stage-amt { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 11.5px; color: var(--mj-text-secondary, #71717a); margin-top: 2px; }
        .dp__stage-drop { font-size: 10.5px; color: #b78104; margin-top: 4px; display: inline-flex; align-items: center; gap: 4px; }
        .dp__totals { display: grid; grid-template-columns: auto 1fr auto 1fr auto 1fr; gap: 8px 12px; font-size: 12px; align-items: baseline; }
        @media (max-width: 700px) { .dp__totals { grid-template-columns: auto 1fr; } }
        .dp__totals .dp__k { color: var(--mj-text-secondary, #71717a); }
        .dp__totals .dp__v { font-family: 'JetBrains Mono', ui-monospace, monospace; font-variant-numeric: tabular-nums; color: var(--mj-text-primary, #1a1a1a); }

        .ds__footer { font-size: 11.5px; color: var(--mj-text-secondary, #71717a); padding: 12px 16px; border: 1px dashed var(--mj-border-default, #e0e0e0); border-radius: 8px; }
        .ds__footer code { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 10.5px; padding: 1px 5px; background: var(--mj-bg-page, #f5f5f5); border-radius: 3px; }
    `]
})
export class AnalyticsDataSourcesSection implements OnInit {
    @Input() performanceMetrics: PerformanceMetricRead[] = [];
    @Input() activeIntegrations: CompanyIntegrationRow[] = [];
    private _companyId: string | null = null;
    @Input() set companyId(v: string | null) {
        if (v && v !== this._companyId) {
            this._companyId = v;
            void this.loadDealStats();
        } else if (!v) {
            this._companyId = null;
            this.dealStats = null;
            this.attributionConfig = null;
        }
    }
    get companyId(): string | null { return this._companyId; }
    @Input() navigate: (path: string) => void = () => {};
    @Input() navigateWithId: (path: string, id?: string) => void = () => {};
    @Input() selectedId: string | null = null;
    @Input() userRole: RoleTier | null = null;

    private cdr = inject(ChangeDetectorRef);
    lastRun: AnalyticsPullRunRow | null = null;
    pulling = false;
    pullMessage: string | null = null;

    /** Deal pipeline (#934). */
    dealStats: {
        totalDeals: number;
        wonAmount: number;
        leadToWon: number;
        byStage: Record<DealStage, { count: number; amount: number }>;
    } | null = null;
    attributionConfig: AttributionConfigRead | null = null;

    get cards(): ResolvedCard[] {
        return SOURCE_CATALOG.map(c => this.resolve(c));
    }

    async ngOnInit(): Promise<void> {
        await this.refreshLastRun();
    }

    private async loadDealStats(): Promise<void> {
        if (!this.companyId) return;
        try {
            const rv = new RunView();
            // Deal core comes from crm.Deal — single source of truth, synced
            // daily by BC. The marketing sidecar only carries attribution data.
            const crmResult = await rv.RunView<CrmDealRead>({
                EntityName: 'Deals',
                ExtraFilter: `CompanyID='${this.companyId}'`,
                ResultType: 'simple',
                Fields: ['ID', 'ExternalSystemRecordID', 'DealStage', 'Value', 'CompanyIntegrationID'],
                MaxRows: 5000,
            });

            if (crmResult.Success) {
                const byStage: Record<DealStage, { count: number; amount: number }> = {
                    Lead: { count: 0, amount: 0 }, MQL: { count: 0, amount: 0 }, SQL: { count: 0, amount: 0 },
                    Opportunity: { count: 0, amount: 0 }, ClosedWon: { count: 0, amount: 0 }, ClosedLost: { count: 0, amount: 0 },
                };
                for (const d of crmResult.Results) {
                    const stage = mapCrmStageNameToFunnel(d.DealStage);
                    if (stage) {
                        byStage[stage].count++;
                        byStage[stage].amount += Number(d.Value ?? 0);
                    }
                }
                const total = crmResult.Results.length;
                const won = byStage.ClosedWon.count;
                this.dealStats = {
                    totalDeals: total,
                    wonAmount: byStage.ClosedWon.amount,
                    leadToWon: total > 0 ? won / total : 0,
                    byStage,
                };
            }

            // Attribution config — pick the first row matching any of this company's
            // CompanyIntegration rows. Multi-integration UX is deferred.
            const cfgResult = await rv.RunView<AttributionConfigRead & { CompanyIntegration: string }>({
                EntityName: 'Attribution Configs',
                ResultType: 'simple',
                Fields: ['ID', 'AttributionModel', 'FirstTouchWeight', 'MiddleTouchWeight', 'LastTouchWeight'],
                MaxRows: 10,
            });
            if (cfgResult.Success && cfgResult.Results.length > 0) {
                this.attributionConfig = cfgResult.Results[0];
            }

            // Surface the most recent sidecar sync timestamp (informational; the
            // sidecar holds touchpoints + attribution, the deal core comes from crm).
            // Touchpoint join could happen here later if/when we render per-deal
            // attribution; for now the funnel rollup doesn't need it.
            void await rv.RunView<MarketingSidecarRead>({
                EntityName: 'Deals__marketing',
                ExtraFilter: `CompanyID='${this.companyId}'`,
                ResultType: 'simple',
                Fields: ['ID', 'ExternalDealID', 'LastSyncedAt', 'AttributionJSON'],
                MaxRows: 1,
                OrderBy: 'LastSyncedAt DESC',
            });

            this.cdr.markForCheck();
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            LogError(`[AnalyticsDataSources] loadDealStats failed: ${msg}`);
        }
    }

    /** Funnel cards rendered in template; computes per-stage drop-off. */
    get stageCards(): { stage: DealStage; label: string; count: number; amount: number | null; dropoff: number | null }[] {
        if (!this.dealStats) return [];
        const order: { stage: DealStage; label: string }[] = [
            { stage: 'Lead', label: 'Lead' },
            { stage: 'MQL', label: 'MQL' },
            { stage: 'SQL', label: 'SQL' },
            { stage: 'Opportunity', label: 'Opportunity' },
            { stage: 'ClosedWon', label: 'Closed-Won' },
            { stage: 'ClosedLost', label: 'Closed-Lost' },
        ];
        return order.map((s, i) => {
            const cur = this.dealStats!.byStage[s.stage];
            // Drop-off: deals that reached this stage but didn't progress to next.
            // For ClosedLost the math doesn't apply — show null.
            let dropoff: number | null = null;
            if (i < 3) {
                const next = this.dealStats!.byStage[order[i + 1].stage];
                if (cur.count > 0) dropoff = Math.max(0, 1 - next.count / cur.count);
            }
            return { stage: s.stage, label: s.label, count: cur.count, amount: cur.amount > 0 ? cur.amount : null, dropoff };
        });
    }

    get attributionLabel(): string {
        const c = this.attributionConfig;
        if (!c) return 'PositionBased 40/20/40 (default)';
        if (c.AttributionModel === 'PositionBased') {
            return `${c.AttributionModel} ${c.FirstTouchWeight}/${c.MiddleTouchWeight}/${c.LastTouchWeight}`;
        }
        return c.AttributionModel;
    }

    get bannerVariant(): BannerVariant {
        if (this.pulling) return 'running';
        if (!this.lastRun) return 'idle';
        if (this.lastRun.Status === 'Failed' || this.lastRun.Success === false) return 'fail';
        if (this.lastRun.Status === 'Running') return 'running';
        return 'ok';
    }

    get bannerIcon(): string {
        switch (this.bannerVariant) {
            case 'ok': return 'fa-circle-check';
            case 'fail': return 'fa-circle-exclamation';
            case 'running': return 'fa-spinner fa-spin';
            case 'warn': return 'fa-triangle-exclamation';
            default: return 'fa-clock';
        }
    }

    get bannerTitle(): string {
        if (this.pulling) return 'Pulling metrics…';
        if (!this.lastRun) return 'Analytics Pull has never run';
        if (this.lastRun.Status === 'Failed' || this.lastRun.Success === false) return 'Last pull failed';
        if (this.lastRun.Status === 'Running') return 'Pull in progress';
        return 'Last pull succeeded';
    }

    get bannerSub(): string {
        if (this.pullMessage) return this.pullMessage;
        if (!this.lastRun) return 'Daily 7am CT (Marketing - Analytics Pull). Use Pull now to trigger immediately.';
        const ts = this.lastRun.CompletedAt || this.lastRun.StartedAt;
        const when = ts ? this.formatRelative(new Date(ts)) : 'unknown';
        if (this.lastRun.Status === 'Failed' || this.lastRun.Success === false) {
            const err = this.lastRun.ErrorMessage || 'Unknown error';
            return `${when} · ${err.slice(0, 200)}`;
        }
        return `${when} · Daily 7am CT cron`;
    }

    async onPullNow(): Promise<void> {
        if (this.pulling || !this.companyId) return;
        this.pulling = true;
        this.pullMessage = null;
        this.cdr.detectChanges();
        try {
            const r = await runAction('Pull Performance Metrics', [
                { Name: 'CompanyID', Value: this.companyId },
                { Name: 'Source', Value: 'GA4' },
            ]);
            this.pullMessage = r.success
                ? `Success · ${r.message}`
                : `Failed · ${r.message}`;
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            LogError(`[AnalyticsDataSources] Pull now failed: ${msg}`);
            this.pullMessage = `Failed · ${msg}`;
        } finally {
            this.pulling = false;
            await this.refreshLastRun();
            this.cdr.detectChanges();
        }
    }

    private async refreshLastRun(): Promise<void> {
        try {
            const rv = new RunView();
            const result = await rv.RunView<AnalyticsPullRunRow & { ScheduledJob: string }>({
                EntityName: 'MJ: Scheduled Job Runs',
                ExtraFilter: `ScheduledJob='Marketing - Analytics Pull'`,
                OrderBy: 'StartedAt DESC',
                MaxRows: 1,
                ResultType: 'simple',
            });
            if (result.Success && result.Results.length > 0) {
                this.lastRun = result.Results[0];
            }
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            LogError(`[AnalyticsDataSources] refreshLastRun failed: ${msg}`);
        }
    }

    private resolve(c: SourceCard): ResolvedCard {
        const integration = c.integration
            ? this.activeIntegrations.find(i => i.Integration === c.integration && i.IsActive)
            : null;
        const metricsForPlatform = c.platform
            ? this.performanceMetrics.filter(m => m.Platform === c.platform)
            : [];

        const sevenAgo = new Date();
        sevenAgo.setUTCHours(0, 0, 0, 0);
        sevenAgo.setUTCDate(sevenAgo.getUTCDate() - 7);
        const recent = metricsForPlatform.filter(m => new Date(m.MetricDate as unknown as string) >= sevenAgo);

        const lastSync = metricsForPlatform.length === 0
            ? null
            : metricsForPlatform.reduce((latest, m) => {
                const d = new Date(m.MetricDate as unknown as string);
                return d > latest ? d : latest;
            }, new Date(0));

        let status: SourceStatus;
        if (recent.length > 0) status = 'healthy';
        else if (metricsForPlatform.length > 0 && integration) status = 'connected';
        else if (integration) status = c.fallbackStatus === 'gap' ? 'gap' : 'connected';
        else status = c.fallbackStatus;

        return {
            ...c,
            status,
            rows7d: c.platform ? recent.length : null,
            rowsTotal: c.platform ? metricsForPlatform.length : null,
            lastSyncDisplay: lastSync ? this.formatRelative(lastSync) : 'Never',
            healthBadge: this.badgeFor(status),
        };
    }

    private badgeFor(status: SourceStatus): { text: string; variant: 'good' | 'warn' | 'bad' | 'muted' | 'info' } {
        switch (status) {
            case 'healthy': return { text: 'Healthy', variant: 'good' };
            case 'connected': return { text: 'Connected', variant: 'info' };
            case 'pending': return { text: 'Pending', variant: 'warn' };
            case 'gap': return { text: 'Gap', variant: 'warn' };
            case 'unconnected': return { text: 'Not connected', variant: 'muted' };
        }
    }

    private formatRelative(d: Date): string {
        const diffMs = Date.now() - d.getTime();
        const minutes = Math.floor(diffMs / 60_000);
        if (minutes < 1) return 'just now';
        if (minutes < 60) return `${minutes}m ago`;
        const hours = Math.floor(minutes / 60);
        if (hours < 24) return `${hours}h ago`;
        const days = Math.floor(hours / 24);
        return `${days}d ago`;
    }
}
